import React, { useRef, useState, useEffect, useCallback } from "react";
import { User } from "firebase/auth";
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SendIcon from '@mui/icons-material/Send';
import { addMaterial, editMaterial, db, getAppUser } from "../serverlib/db";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import { Material, Subject, AppUser } from "../serverlib/util-types";
import { useTranslation } from "react-i18next";
import "../styles/editorCustomStyles.css";
import "../styles/admin.css";

// ---------------------------------------------------------------------------
// Custom Image blot — extends Embed (not Inline, not BlockEmbed) so the
// toolbar image button works AND text can flow around floated images.
// Preserves width / height / style attributes across save/load.
// ---------------------------------------------------------------------------
const Embed = Quill.import('blots/embed') as any;

class ImageBlot extends Embed {
    static blotName = 'image';
    static tagName  = 'img';

    static create(value: any) {
        const node = super.create(value) as HTMLElement;
        const src = typeof value === 'string' ? value : value?.src;
        if (src)            node.setAttribute('src',    src);
        if (value?.alt)     node.setAttribute('alt',    value.alt);
        if (value?.width)   node.setAttribute('width',  value.width);
        if (value?.height)  node.setAttribute('height', value.height);
        if (value?.style)   node.setAttribute('style',  value.style);
        // default cursor so it's clear the image is interactive
        (node as HTMLElement).style.cursor = 'pointer';
        return node;
    }

    static value(node: HTMLElement) {
        return {
            src:    node.getAttribute('src')    ?? '',
            alt:    node.getAttribute('alt')    ?? '',
            width:  node.getAttribute('width')  ?? '',
            height: node.getAttribute('height') ?? '',
            style:  node.getAttribute('style')  ?? '',
        };
    }

    static formats(node: HTMLElement) {
        return {
            width:  node.getAttribute('width')  ?? '',
            height: node.getAttribute('height') ?? '',
            style:  node.getAttribute('style')  ?? '',
        };
    }

    format(name: string, value: string) {
        if (['width', 'height', 'style'].includes(name)) {
            if (value) this.domNode.setAttribute(name, value);
            else       this.domNode.removeAttribute(name);
        } else {
            super.format(name, value);
        }
    }
}

Quill.register(ImageBlot, true);

// ---------------------------------------------------------------------------
// Image interaction module — click to select, drag handles to resize,
// toolbar buttons to set float / block layout. Touch-first (iPad priority).
// ---------------------------------------------------------------------------
type Handle = 'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w';
const HANDLES: Handle[] = ['nw','n','ne','e','se','s','sw','w'];
const CURSOR: Record<Handle, string> = {
    nw:'nw-resize', n:'n-resize', ne:'ne-resize', e:'e-resize',
    se:'se-resize', s:'s-resize', sw:'sw-resize', w:'w-resize',
};

class ImageInteractModule {
    quill: any;
    img: HTMLImageElement | null = null;
    overlay: HTMLElement | null = null;
    activeHandle: Handle | null = null;
    startX = 0; startY = 0; startW = 0; startH = 0;

    constructor(quill: any) {
        this.quill = quill;
        this.onMouseDown  = this.onMouseDown.bind(this);
        this.onMouseMove  = this.onMouseMove.bind(this);
        this.onMouseUp    = this.onMouseUp.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove  = this.onTouchMove.bind(this);
        this.onTouchEnd   = this.onTouchEnd.bind(this);

        quill.root.addEventListener('click', (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            if (t.tagName === 'IMG') this.select(t as HTMLImageElement);
            else if (!this.overlay?.contains(t)) this.deselect();
        });

        document.addEventListener('mousedown', (e: MouseEvent) => {
            if (
                this.overlay &&
                !quill.root.contains(e.target as Node) &&
                !this.overlay.contains(e.target as Node)
            ) this.deselect();
        });

        // Delete selected image on Backspace / Delete key
        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (this.img && (e.key === 'Backspace' || e.key === 'Delete')) {
                e.preventDefault();
                const blot = Quill.find(this.img) as any;
                if (blot && typeof blot.remove === 'function') blot.remove();
                this.deselect();
            }
        });

        // Deselect immediately if the selected image is removed from the DOM
        const mo = new MutationObserver(() => {
            if (this.img && !quill.root.contains(this.img)) this.deselect();
        });
        mo.observe(quill.root, { childList: true, subtree: true });
    }

    select(img: HTMLImageElement) {
        this.deselect();
        this.img = img;
        this.buildOverlay();
    }

    deselect() {
        this.overlay?.remove();
        this.overlay = null;
        this.img = null;
    }

    // ── Overlay ───────────────────────────────────────────────────────────────
    buildOverlay() {
        const overlay = document.createElement('div');
        overlay.className = 'ql-img-overlay';
        overlay.style.cssText = [
            'position:absolute',
            'pointer-events:none',
            'box-sizing:border-box',
            'border:2px solid #4a90d9',
            'z-index:200',
        ].join(';');
        this.overlay = overlay;

        // 8 resize handles
        HANDLES.forEach(h => {
            const el = document.createElement('div');
            el.dataset.handle = h;
            const isN = h.includes('n'), isS = h.includes('s');
            const isW = h.includes('w'), isE = h.includes('e');
            el.style.cssText = [
                'position:absolute',
                'width:12px', 'height:12px',
                'background:#4a90d9',
                'border:2px solid #fff',
                'border-radius:50%',
                'pointer-events:all',
                `cursor:${CURSOR[h]}`,
                'touch-action:none',
                'box-sizing:border-box',
                isN ? 'top:-7px'    : isS ? 'bottom:-7px'       : 'top:calc(50% - 6px)',
                isW ? 'left:-7px'   : isE ? 'right:-7px'         : 'left:calc(50% - 6px)',
            ].join(';');
            el.addEventListener('mousedown',  this.onMouseDown);
            el.addEventListener('touchstart', this.onTouchStart, { passive: false });
            overlay.appendChild(el);
        });

        // Float toolbar above the image
        const bar = document.createElement('div');
        bar.style.cssText = 'position:absolute;top:-42px;left:0;display:flex;gap:4px;pointer-events:all;white-space:nowrap;';
        const bStyle = [
            'padding:4px 10px', 'font-size:12px', 'cursor:pointer',
            'border-radius:4px', 'border:1px solid #4a90d9',
            'background:#fff', 'color:#333', 'font-family:sans-serif',
            'line-height:1.4', 'touch-action:manipulation',
            '-webkit-tap-highlight-color:transparent',
        ].join(';');
        [['← Left','left'],['■ Block','block'],['Right →','right']].forEach(([label, mode]) => {
            const btn = document.createElement('button');
            btn.textContent = label;
            btn.style.cssText = bStyle;
            btn.addEventListener('mousedown',  e => { e.preventDefault(); this.applyFloat(mode); });
            btn.addEventListener('touchend',   e => { e.preventDefault(); this.applyFloat(mode); });
            bar.appendChild(btn);
        });
        overlay.appendChild(bar);

        // Attach to ql-container (which is position:relative)
        const container = (this.quill.root as HTMLElement).parentElement!;
        container.style.position = 'relative';
        container.appendChild(overlay);
        this.reposition();
    }

    reposition() {
        if (!this.img || !this.overlay) return;
        const container = this.overlay.parentElement!;
        // Walk the offset chain relative to the container for scroll-proof positioning
        let offTop = 0, offLeft = 0;
        let el: HTMLElement | null = this.img;
        while (el && el !== container) {
            offTop  += el.offsetTop;
            offLeft += el.offsetLeft;
            el = el.offsetParent as HTMLElement | null;
        }
        this.overlay.style.top    = `${offTop}px`;
        this.overlay.style.left   = `${offLeft}px`;
        this.overlay.style.width  = `${this.img.offsetWidth}px`;
        this.overlay.style.height = `${this.img.offsetHeight}px`;
    }

    // ── Float ────────────────────────────────────────────────────────────────
    applyFloat(mode: string) {
        if (!this.img) return;
        let style = (this.img.getAttribute('style') || '')
            .replace(/float\s*:[^;]*;?/gi, '')
            .replace(/display\s*:[^;]*;?/gi, '')
            .replace(/margin\s*:[^;]*;?/gi, '')
            .trim();
        if (mode === 'left')  style = `float:left;margin:0 12px 8px 0;${style}`;
        else if (mode === 'right') style = `float:right;margin:0 0 8px 12px;${style}`;
        else                  style = `display:block;margin:8px auto;${style}`;
        this.img.setAttribute('style', style);
        this.persist();
        this.reposition();
    }

    // ── Resize ───────────────────────────────────────────────────────────────
    startDrag(handle: Handle, cx: number, cy: number) {
        if (!this.img) return;
        this.activeHandle = handle;
        this.startX = cx; this.startY = cy;
        this.startW = this.img.offsetWidth;
        this.startH = this.img.offsetHeight;
        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup',   this.onMouseUp);
        document.addEventListener('touchmove', this.onTouchMove,  { passive: false });
        document.addEventListener('touchend',  this.onTouchEnd);
    }

    doResize(cx: number, cy: number) {
        if (!this.img || !this.activeHandle) return;
        const dx = cx - this.startX, dy = cy - this.startY;
        const h = this.activeHandle;
        let w = this.startW, he = this.startH;
        if (h.includes('e')) w  = Math.max(20, this.startW + dx);
        if (h.includes('w')) w  = Math.max(20, this.startW - dx);
        if (h.includes('s')) he = Math.max(20, this.startH + dy);
        if (h.includes('n')) he = Math.max(20, this.startH - dy);
        this.img.style.width  = `${w}px`;
        this.img.style.height = `${he}px`;
        this.img.setAttribute('width',  String(w));
        this.img.setAttribute('height', String(he));
        this.reposition();
    }

    endDrag() {
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup',   this.onMouseUp);
        document.removeEventListener('touchmove', this.onTouchMove);
        document.removeEventListener('touchend',  this.onTouchEnd);
        this.activeHandle = null;
        this.persist();
        if (this.img) this.reposition();
    }

    onMouseDown(e: MouseEvent) {
        e.preventDefault();
        this.startDrag((e.currentTarget as HTMLElement).dataset.handle as Handle, e.clientX, e.clientY);
    }
    onTouchStart(e: TouchEvent) {
        e.preventDefault();
        const t = e.touches[0];
        this.startDrag((e.currentTarget as HTMLElement).dataset.handle as Handle, t.clientX, t.clientY);
    }
    onMouseMove(e: MouseEvent) { this.doResize(e.clientX, e.clientY); }
    onTouchMove(e: TouchEvent) { e.preventDefault(); this.doResize(e.touches[0].clientX, e.touches[0].clientY); }
    onMouseUp()  { this.endDrag(); }
    onTouchEnd() { this.endDrag(); }

    // ── Persist attributes into Quill delta ──────────────────────────────────
    persist() {
        if (!this.img) return;
        const blot = (this.quill.constructor as any).find(this.img);
        if (!blot) return;
        const style  = this.img.getAttribute('style')  || '';
        const width  = this.img.getAttribute('width')  || '';
        const height = this.img.getAttribute('height') || '';
        if (style)  blot.format('style',  style);
        if (width)  blot.format('width',  width);
        if (height) blot.format('height', height);
    }
}

Quill.register('modules/imageInteract', ImageInteractModule);

// ---------------------------------------------------------------------------

interface TextEditorProps {
    user: User | null;
    subjectfromurl: Subject | null;
    gradefromurl: string | null;
    textMatTopic: string | null;
    textMatTopicUnit: string | null;
    textMatTopicSubtopic1: string | null;
}

const PLACEHOLDER_CONTENT = "<h1>Ders sayfasını oluşturmaya başlamak için burayı düzenleyin.</h1><br/><h2>Editer ici pour créer la page de cours</h2>";
const CANVAS_WIDTH = 800;

// Maximum individual image size after compression (bytes)
const TEXTMAT_IMAGE_MAX_BYTES = 100_000; // 100 KB
// Target quality reduction — percentage of original dimensions to start with
const TEXTMAT_IMAGE_BASE64_SIZE_PERCENTAGE = 40;
// Hard cap on entire text material document (Firestore limit 1 MB)
const TEXTMAT_MAX_DOC_BYTES = 1_000_000; // 1 MB
const TEXTMAT_MAX_DOC_KBYTES_STRING = (TEXTMAT_MAX_DOC_BYTES / 1000).toLocaleString() + " KB";
const TEXTMAT_WARN_THRESHOLD_KB = Math.round(TEXTMAT_MAX_DOC_BYTES * 0.9 / 1024); // 90% of limit

/**
 * Compress an image file to a JPEG base64 data-URL under TEXTMAT_IMAGE_MAX_BYTES.
 * Uses native Canvas API — no external dependencies.
 */
function compressImageToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error('Failed to load image'));
            img.onload = () => {
                // Start with scaled-down dimensions
                let scale = TEXTMAT_IMAGE_BASE64_SIZE_PERCENTAGE / 100;
                let quality = 0.7;
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d')!;

                const attempt = (): string => {
                    canvas.width  = Math.max(1, Math.round(img.width  * scale));
                    canvas.height = Math.max(1, Math.round(img.height * scale));
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    return canvas.toDataURL('image/jpeg', quality);
                };

                let dataUrl = attempt();

                // If still too large, progressively reduce quality and scale
                let tries = 0;
                while (dataUrl.length > TEXTMAT_IMAGE_MAX_BYTES * 1.37 && tries < 8) {
                    // 1.37 ≈ base64 overhead (4/3) — compare encoded length to byte budget
                    quality = Math.max(0.1, quality - 0.1);
                    if (quality <= 0.15) {
                        scale *= 0.7;
                        quality = 0.5;
                    }
                    dataUrl = attempt();
                    tries++;
                }

                resolve(dataUrl);
            };
            img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
    });
}

const TextEditor = (props: TextEditorProps) => {
    const user = props.user;
    const { t } = useTranslation("common");
    const [appUser, setAppUser]     = useState<AppUser | null>(null);
    const [content, setContent]     = useState<string>(PLACEHOLDER_CONTENT);
    const [materialType]            = useState("text");
    const [materialName]            = useState("");
    const [subject, setSubject]     = useState<Subject | ''>('');
    const [grade, setGrade]         = useState("");
    const [teacherinfo, setTeacherInfo] = useState<string[]>([]);
    const quillRef   = useRef<ReactQuill>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const canvasRef  = useRef<HTMLDivElement>(null);

    const [topic, setTopic]               = useState<string>(props.textMatTopic || "");
    const [textTypeMaterial, setTextTypeMaterial] = useState<any[]>([]);
    const [editorInitialized, setEditorInitialized] = useState(false);

    useEffect(() => { setEditorInitialized(true); }, []);

    const updateScale = useCallback(() => {
        const wrapper = wrapperRef.current;
        const canvas  = canvasRef.current;
        if (!wrapper || !canvas) return;
        const available = wrapper.clientWidth;
        if (available === 0) return;
        const sc = Math.min(1, available / CANVAS_WIDTH);
        // Measure natural height at scale(1) first
        canvas.style.transform = 'none';
        const naturalH = canvas.scrollHeight;
        canvas.style.transform       = `scale(${sc})`;
        canvas.style.transformOrigin = 'top left';
        canvas.style.marginLeft      = `${(available - CANVAS_WIDTH * sc) / 2}px`;
        wrapper.style.overflow = 'hidden';
        wrapper.style.height   = `${naturalH * sc}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const canvas  = canvasRef.current;
        if (!wrapper || !canvas) return;
        let raf = 0;
        const scheduled = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(updateScale); };
        const ro = new ResizeObserver(scheduled);
        ro.observe(wrapper);
        ro.observe(canvas);
        // Subscribe to Quill's editor-change — fires after Quill updates its DOM.
        // Re-runs whenever appUser changes so we catch the initial Quill mount.
        const quill = quillRef.current?.getEditor();
        if (quill) {
            quill.on('editor-change', scheduled);
        }
        // Fire when popup goes from display:none to display:block
        const popup = wrapper.closest('.texteditor-popup') as HTMLElement | null;
        let mo: MutationObserver | null = null;
        if (popup) {
            mo = new MutationObserver(() => { if (popup.style.display !== 'none') scheduled(); });
            mo.observe(popup, { attributes: true, attributeFilter: ['style'] });
        }
        return () => {
            ro.disconnect();
            mo?.disconnect();
            cancelAnimationFrame(raf);
            if (quill) quill.off('editor-change', scheduled);
        };
    // appUser is the gate that renders ReactQuill — re-run when it loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateScale, appUser]);

    const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const [justAdded, setJustAdded] = useState(false);

    const handleGetTextTypeMaterial = useCallback(async () => {
        if (!props.user || !subject || !grade || !topic) return;
        try {
            const colRef   = collection(db, 'materials', subject, grade);
            const docSnap  = await getDocs(colRef);
            const data     = docSnap.docs.map(d => d.data() as Material);
            const textMats = data.filter(m => m.materialtype === "text" && m.topic === topic);
            setTextTypeMaterial(textMats);
            if (textMats.length > 0) {
                setContent(textMats[0].content);
            } else {
                setContent(PLACEHOLDER_CONTENT);
            }
        } catch (err) {
            console.error('Error fetching material:', err);
            setContent(PLACEHOLDER_CONTENT);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject, grade, topic, props.user]);

    useEffect(() => {
        if (editorInitialized) handleGetTextTypeMaterial();
    }, [editorInitialized, topic, subject, grade, handleGetTextTypeMaterial]);

    useEffect(() => {
        getAppUser(props.user?.uid!).then(au => { if (au) setAppUser(au as AppUser); });
        if (user) setTeacherInfo([user.uid, user.providerData[0].displayName!, user.providerData[0].email!]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.user]);

    useEffect(() => {
        if (props.textMatTopic   !== null) setTopic(props.textMatTopic!);
        if (props.subjectfromurl !== null) setSubject(props.subjectfromurl!);
        if (props.gradefromurl   !== null) setGrade(props.gradefromurl!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.textMatTopic, props.subjectfromurl, props.gradefromurl]);

    useEffect(() => { handleGetTextTypeMaterial(); }, [topic, handleGetTextTypeMaterial]);
    useEffect(() => { if (content === "") setContent(PLACEHOLDER_CONTENT); }, [content]);

    // Scale is driven by Quill's editor-change event (see observer useEffect above).
    // No setTimeout needed — editor-change fires after Quill has updated its DOM.

    const handleSave = async () => {
        if (!props.user || !content || content === PLACEHOLDER_CONTENT) return;
        if (!subject || !grade || !materialType || !topic) return;
        setSizeError(null);
        setSizeWarning(null);

        // Check total document size (Firestore 1 MB limit) — blocks save
        const docBytes = new Blob([content]).size;
        if (docBytes > TEXTMAT_MAX_DOC_BYTES) {
            setSizeError(t("admin.textmat-size-error"));
            return;
        }

        // Check individual images — yellow warning only, still allows save
        const imgMatches = content.match(/src="data:image[^"]+"/g) || [];
        for (const m of imgMatches) {
            const dataUrl = m.slice(5, -1); // strip src=" and trailing "
            const byteLen = Math.round(dataUrl.length * 3 / 4);
            if (byteLen > TEXTMAT_IMAGE_MAX_BYTES) {
                setSizeWarning(t("admin.textmat-image-too-large"));
                break;
            }
        }

        try {
            const existing = textTypeMaterial.find(m => m.topic === topic);
            if (existing || justAdded) {
                await editMaterial(subject, grade, 'text', content, topic, "", true, existing);
                setShowUpdateConfirm(true);
                setTimeout(() => setShowUpdateConfirm(false), 3000);
            } else {
                await addMaterial(materialName, subject, grade, content, teacherinfo, 'text', topic, Timestamp.now());
                setJustAdded(true);
                setShowSaveConfirm(true);
                setTimeout(() => setShowSaveConfirm(false), 3000);
            }
        } catch (err) {
            console.error('Error saving text material:', err);
            setSizeError(t("admin.textmat-save-error"));
        }
    };

    const [editorVisible, setEditorVisible] = useState(true);
    const [sizeWarning, setSizeWarning] = useState<string | null>(null);  // yellow, non-blocking
    const [sizeError, setSizeError] = useState<string | null>(null);      // red, blocks save
    const [imgPickerOpen, setImgPickerOpen] = useState(false);
    const [imgUrlValue, setImgUrlValue] = useState('');
    const savedRange = React.useRef<{ index: number; length: number } | null>(null);

    // Real-time document size in KB
    const docSizeKB = React.useMemo(() => {
        return Math.round(new Blob([content]).size / 1024);
    }, [content]);

    // Opens the image picker modal, saving cursor position first
    const imageHandler = useCallback(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        savedRange.current = quill.getSelection(true);
        setImgUrlValue('');
        setImgPickerOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Insert image from URL — same ImageBlot as file uploads, full resize/float support
    const insertImageUrl = useCallback(() => {
        const url = imgUrlValue.trim();
        if (!url) return;
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const index = savedRange.current?.index ?? quill.getLength();
        quill.insertEmbed(index, 'image', url, 'user');
        quill.setSelection(index + 1, 0);
        setImgPickerOpen(false);
    }, [imgUrlValue]);

    // Insert image from file — compress then insert
    const insertImageFile = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        setImgPickerOpen(false);
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            const quill = quillRef.current?.getEditor();
            if (!quill) return;
            const index = savedRange.current?.index ?? quill.getLength();

            // Insert temp full-res image and grab a direct reference to the DOM node
            let insertedImg: HTMLImageElement | null = null;
            await new Promise<void>(resolve => {
                const tempReader = new FileReader();
                tempReader.onload = () => {
                    quill.insertEmbed(index, 'image', tempReader.result as string, 'user');
                    quill.setSelection(index + 1, 0);
                    // The inserted node is at position `index` in the editor DOM
                    const leaf = quill.getLeaf(index + 1);
                    insertedImg = (leaf?.[0]?.domNode as HTMLImageElement) ?? null;
                    resolve();
                };
                tempReader.readAsDataURL(file);
            });

            // Compress and swap ONLY the specific node we just inserted
            try {
                const compressed = await compressImageToBase64(file);
                if (insertedImg) {
                    (insertedImg as HTMLImageElement).setAttribute('src', compressed);
                } else {
                    // Fallback: find the freshly inserted node by matching the uncompressed src
                    const imgs = quill.root.querySelectorAll('img');
                    for (let i = imgs.length - 1; i >= 0; i--) {
                        const src = imgs[i].getAttribute('src') || '';
                        if (src.startsWith('data:') && src !== compressed) {
                            imgs[i].setAttribute('src', compressed);
                            break;
                        }
                    }
                }
                setContent(quill.root.innerHTML);
            } catch (err) {
                console.error('Image compression failed:', err);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const quillModules = React.useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }, { background: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ align: [] }],
                ['link', 'image', 'video'],
                ['blockquote', 'code-block'],
                ['clean'],
            ],
            handlers: {
                image: imageHandler,
            },
        },
        imageInteract: {},
    }), [imageHandler]);

    const quillFormats = [
        'header', 'bold', 'italic', 'underline', 'strike',
        'color', 'background', 'list', 'bullet', 'indent', 'align',
        'link', 'image', 'video', 'blockquote', 'code-block',
        'width', 'height', 'style',
    ];

    if (!appUser) {
        return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>...</div>;
    }

    if (appUser && (appUser.isAdmin || appUser.accesslevel === "teacher")) {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                {props.subjectfromurl !== null && props.gradefromurl !== null && props.textMatTopic !== null &&
                    <div className="admin-widget" style={{ width: "100%", boxSizing: "border-box" }}>
                        <div className="admin-widget-title" onClick={() => setEditorVisible(v => !v)}>
                            <b>🖊️ {t("footer.texteditor")}</b>
                            <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.6, marginLeft: 8, color: docSizeKB > TEXTMAT_WARN_THRESHOLD_KB ? 'var(--error-color, #e05252)' : 'inherit' }}>
                                {docSizeKB} KB / {TEXTMAT_MAX_DOC_KBYTES_STRING}
                            </span>
                        </div>
                        <div className={`admin-widget-content ${editorVisible ? 'visible' : ''}`}>
                            <hr />
                            <div className="admin-subwidget">
                                {/* Measure available width, center the canvas inside */}
                                <div ref={wrapperRef} className="quill-editor-wrapper">
                                    <div ref={canvasRef} style={{ width: CANVAS_WIDTH }}>
                                        <ReactQuill
                                            ref={quillRef}
                                            theme="snow"
                                            value={content}
                                            onChange={(val) => { setContent(val); }}
                                            modules={quillModules}
                                            formats={quillFormats}
                                            placeholder="Ders sayfasını oluşturmaya başlamak için burayı düzenleyin."
                                        />
                                    </div>
                                </div>
                                {/* Image picker modal */}
                                {imgPickerOpen && (
                                    <div
                                        onClick={() => setImgPickerOpen(false)}
                                        style={{
                                            position: 'fixed', inset: 0, zIndex: 3000,
                                            background: 'rgba(0,0,0,0.55)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            padding: 24,
                                        }}
                                    >
                                        <div
                                            onClick={e => e.stopPropagation()}
                                            style={{
                                                background: 'var(--grey-secondary)',
                                                borderRadius: 12,
                                                padding: '20px 20px',
                                                width: '100%',
                                                maxWidth: 380,
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 12,
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <b style={{ fontSize: 14 }}>🖼️ {t("admin.insert-image")}</b>
                                                <button className="mat-list-btn hover-up" type="button" onClick={() => setImgPickerOpen(false)}>✕</button>
                                            </div>
                                            {/* URL row — input takes remaining space, ✓ button is icon-sized */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <input
                                                    type="url"
                                                    autoFocus
                                                    placeholder={t("admin.insert-image-url-placeholder")}
                                                    value={imgUrlValue}
                                                    onChange={e => setImgUrlValue(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') insertImageUrl(); if (e.key === 'Escape') setImgPickerOpen(false); }}
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        background: 'var(--grey-tertiary)',
                                                        color: 'var(--text-primary)',
                                                        border: 'none',
                                                        borderBottom: '2px solid var(--accent-color-dark)',
                                                        borderRadius: 4,
                                                        padding: '7px 8px',
                                                        fontSize: 14,
                                                        outline: 'none',
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={insertImageUrl}
                                                    style={{
                                                        flexShrink: 0,
                                                        width: 36, height: 36,
                                                        border: 'none',
                                                        borderRadius: 6,
                                                        background: 'var(--accent-color)',
                                                        color: '#fff',
                                                        fontSize: 16,
                                                        cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}
                                                >✓</button>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.5, fontSize: 12 }}>
                                                <hr style={{ flex: 1, margin: 0 }} />
                                                {t("admin.insert-image-or")}
                                                <hr style={{ flex: 1, margin: 0 }} />
                                            </div>
                                            <button className="admin-button hover-up" type="button" onClick={insertImageFile}>
                                                📁 {t("admin.insert-image-file")}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {sizeWarning && (
                                    <p style={{ color: '#d4a017', fontSize: 13, margin: '8px 0' }}>
                                        ⚠️ {sizeWarning}
                                    </p>
                                )}
                                {sizeError && (
                                    <p style={{ color: 'var(--error-color, #e05252)', fontSize: 13, margin: '8px 0', fontWeight: 600 }}>
                                        🚫 {sizeError}
                                    </p>
                                )}
                                <button
                                    className={`${showUpdateConfirm || showSaveConfirm ? 'admin-button-confirm' : ''} admin-button colored-button hover-up`}
                                    onClick={handleSave}
                                >
                                    <SendIcon />
                                    {showUpdateConfirm || showSaveConfirm ? t("admin.mat-published") : t("admin.mat-publish")}
                                </button>
                            </div>
                        </div>
                    </div>
                }
            </div>
        );
    } else if (appUser?.accesslevel === "student" && appUser?.isAdmin === false) {
        return (
            <div className="page-wrapper admin-wrapper">
                <div className="admin-widget">
                    <div className="admin-widget-title">
                        <b>🚫 Erişim Engellendi</b> / Accès refusé <br />
                        <b>🚫 Yetkisiz kullanıcı</b> / Utilisateur non autorisé <br />
                        🚫 <i>Access denied</i> <br /> <hr />
                        <b>Yönetici veya Öğretmen Yetkisi Gerekiyor</b> / Accès réservé aux administrateurs et enseignants
                    </div>
                </div>
            </div>
        );
    } else {
        return null;
    }
};

export default TextEditor;
